/**
 * Pure utility functions for overlay/tile URL construction.
 * Extracted from cairn-map.ts for testability.
 */

export interface WmsOptions {
  layers: string
  format?: string
  transparent?: boolean
  time?: string
}

/**
 * Format TIME parameter for GIBS WMS.
 * GIBS WMS only accepts YYYY-MM-DD format for all layers, including GOES.
 * Clamps to (today - latencyDays) if the requested date is too recent.
 *
 * @param date - The requested date
 * @param latencyDays - Processing latency in days; clamp if date is too recent
 * @param now - Current time override for testing (defaults to new Date())
 */
export function formatWmsTime(date: Date, latencyDays = 0, now?: Date): string {
  if (latencyDays > 0) {
    const ref = now ?? new Date()
    const cutoff = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate())
    cutoff.setDate(cutoff.getDate() - latencyDays)
    if (date > cutoff) {
      return cutoff.toISOString().split('T')[0]
    }
  }
  return date.toISOString().split('T')[0]
}

/**
 * Format TIME for GIBS WMTS subdaily layers (GOES).
 * Snaps to 20-minute boundaries (0, 20, 40) for reliable coverage.
 * GOES imagery has ~90 min processing latency, so clamp to 2 hours behind now.
 *
 * @param date - The requested date/time
 * @param nowMs - Current epoch ms override for testing (defaults to Date.now())
 */
export function formatWmtsSubdailyTime(date: Date, nowMs?: number): string {
  const snapped = new Date(date)
  // Clamp: don't request anything more recent than 2 hours ago
  const cutoff = new Date((nowMs ?? Date.now()) - 2 * 60 * 60 * 1000)
  if (snapped > cutoff) {
    snapped.setTime(cutoff.getTime())
  }
  // Snap to 20-min boundaries (0, 20, 40) — these have the most reliable coverage
  snapped.setMinutes(Math.floor(snapped.getMinutes() / 20) * 20, 0, 0)
  return snapped.toISOString().replace('.000Z', 'Z')
}

/**
 * Expand a tile URL containing `{s}` into an array of URLs with subdomain substitution.
 * If the URL does not contain `{s}`, returns a single-element array.
 *
 * @param url - Tile URL template, possibly containing `{s}`
 * @param subdomains - Subdomain characters; defaults to ['a','b','c']
 */
export function expandTileUrl(url: string, subdomains?: string | string[]): string[] {
  if (!url.includes('{s}')) {
    return [url]
  }
  const subs = subdomains
    ? typeof subdomains === 'string'
      ? subdomains.split('')
      : subdomains
    : ['a', 'b', 'c']
  return subs.map(s => url.replace('{s}', s))
}

/**
 * Build a WMS GetMap tile URL template for use with raster tile sources.
 * The returned URL contains `{bbox-epsg-3857}` as a placeholder for the tile bounding box.
 *
 * @param baseUrl - The WMS service base URL
 * @param wmsOptions - WMS layer options (layers, format, transparent, etc.)
 * @param time - Optional TIME parameter value
 */
export function buildWmsTileUrl(baseUrl: string, wmsOptions: WmsOptions, time?: string): string {
  let wmsUrl = baseUrl
  if (!wmsUrl.includes('?')) {
    wmsUrl += '?'
  }
  const params = new URLSearchParams()
  params.set('SERVICE', 'WMS')
  params.set('VERSION', '1.1.1')
  params.set('REQUEST', 'GetMap')
  params.set('LAYERS', wmsOptions.layers)
  params.set('STYLES', '')
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string should use default
  params.set('FORMAT', wmsOptions.format || 'image/png')
  params.set('TRANSPARENT', (wmsOptions.transparent !== false).toString().toUpperCase())
  params.set('SRS', 'EPSG:3857')
  params.set('WIDTH', '256')
  params.set('HEIGHT', '256')
  params.set('BBOX', '{bbox-epsg-3857}')
  if (time) {
    params.set('TIME', time)
  }

  return `${wmsUrl}&${params.toString()}`.replace('%7Bbbox-epsg-3857%7D', '{bbox-epsg-3857}')
}
